// ============================================================
// FONADHOO CRM v3.0 - REMINDERS PANEL COMPONENT
// Automated reminder system for follow-ups and scheduling
// ============================================================

import React, { useState, useEffect } from 'react';
import { REMINDER_TYPES, REMINDER_PRIORITY, REMINDER_RECURRING } from './shared/constants';
import { formatDate, daysUntil, isOverdue, isSoon } from './shared/utils';
import { Icon } from './shared/Icon';

// Maldives phone helpers (same convention as GOTV chase mode): 7-digit local → +960.
const phoneDigits = (p) => (p || '').replace(/\D/g, '');
const waHref = (p, text) => { let d = phoneDigits(p); if (d.length === 7) d = '960' + d; return `https://wa.me/${d}${text ? `?text=${encodeURIComponent(text)}` : ''}`; };
const telHref = (p) => `tel:${phoneDigits(p)}`;

export default function RemindersPanel({ supabase, profile, user }) {
  // App.jsx passes this as `user`; older call sites may pass `profile`.
  profile = profile ?? user;
  const isPatchAgent = profile?.role === 'patch_agent';
  const [reminders, setReminders] = useState([]);
  const [counts, setCounts] = useState({ total: 0, overdue: 0, today: 0, upcoming: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterView, setFilterView] = useState('All'); // Upcoming, Overdue, Completed, All

  // Responsive breakpoint — matches the 767px cutoff used elsewhere in the
  // app (VoterProfile, FamilyTree). Used to keep the header row shaped the
  // same as Events/Trips/Outreach on mobile (single button, no wrapping).
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  // Add form
  const [addForm, setAddForm] = useState({
    voter_id: '',
    voter_name: '',
    reminder_date: '',
    reminder_type: 'Call',
    priority: 'Medium',
    recurring: 'Once',
    message: '',
    assigned_to: profile?.id || ''
  });

  // Voter search
  const [voterSearch, setVoterSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    loadReminders();
  }, [filterView]);

  useEffect(() => {
    if (voterSearch.trim()) {
      searchVoters();
    } else {
      setSearchResults([]);
    }
  }, [voterSearch]);

  const buildFilteredQuery = (view, today) => {
    let q = supabase.from('reminders').select(`
      *,
      voter:voters(id, name, island, ward, house, phone),
      assigned_to_profile:profiles!reminders_assigned_to_fkey(full_name)
    `);
    if (isPatchAgent) q = q.eq('assigned_to', profile.id);
    if (view === 'Upcoming')  return q.eq('completed', false).gt('reminder_date', today).order('reminder_date', { ascending: true });
    if (view === 'Today')     return q.eq('completed', false).eq('reminder_date', today).order('reminder_date', { ascending: true });
    if (view === 'Overdue')   return q.eq('completed', false).lt('reminder_date', today).order('reminder_date', { ascending: true });
    if (view === 'Completed') return q.eq('completed', true).order('completed_at', { ascending: false }).limit(50);
    return q.order('reminder_date', { ascending: false }).limit(100);
  };

  const baseCount = () => {
    let q = supabase.from('reminders').select('*', { count: 'exact', head: true });
    if (isPatchAgent) q = q.eq('assigned_to', profile.id);
    return q;
  };

  const fetchAll = async () => {
    const today = new Date().toISOString().split('T')[0];
    const [{ data, error }, { count: total }, { count: overdue }, { count: today_c }, { count: upcoming }, { count: completed }] = await Promise.all([
      buildFilteredQuery(filterView, today),
      baseCount(),
      baseCount().eq('completed', false).lt('reminder_date', today),
      baseCount().eq('completed', false).eq('reminder_date', today),
      baseCount().eq('completed', false).gt('reminder_date', today),
      baseCount().eq('completed', true),
    ]);
    if (error) console.error('[reminders] list query failed:', error);
    setReminders(data || []);
    setCounts({ total: total ?? 0, overdue: overdue ?? 0, today: today_c ?? 0, upcoming: upcoming ?? 0, completed: completed ?? 0 });
  };

  const loadReminders = async () => {
    setLoading(true);
    await fetchAll();
    setLoading(false);
  };

  // Refresh list + counts without showing the loading spinner (used after complete/delete)
  const silentRefresh = fetchAll;

  const searchVoters = async () => {
    const { data } = await supabase
      .from('voters')
      .select('id, name, island, ward')
      .ilike('name', `%${voterSearch}%`)
      .limit(10);
    
    setSearchResults(data || []);
  };

  const [editingId, setEditingId] = useState(null);
  const [snoozeMenuId, setSnoozeMenuId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };

  const resetForm = () => setAddForm({
    voter_id: '', voter_name: '', reminder_date: '', reminder_type: 'Call',
    priority: 'Medium', recurring: 'Once', message: '', assigned_to: profile?.id || ''
  });

  // Header button: open a fresh form, or close + clear edit state.
  const toggleAdd = () => {
    if (showAdd) { setShowAdd(false); setEditingId(null); return; }
    setEditingId(null); resetForm(); setVoterSearch(''); setShowAdd(true);
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setAddForm({
      voter_id: r.voter_id ? String(r.voter_id) : '',
      voter_name: r.voter?.name || '',
      reminder_date: r.reminder_date || '',
      reminder_type: r.reminder_type || 'Call',
      priority: r.priority || 'Medium',
      recurring: r.recurring || 'Once',
      message: r.message || '',
      assigned_to: r.assigned_to || profile?.id || '',
    });
    setVoterSearch(r.voter?.name || '');
    setSearchResults([]);
    setShowAdd(true);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddReminder = async () => {
    if (!addForm.voter_id || !addForm.reminder_date) {
      alert('Please select a voter and set a reminder date');
      return;
    }

    const payload = {
      voter_id: parseInt(addForm.voter_id),
      reminder_date: addForm.reminder_date,
      reminder_type: addForm.reminder_type,
      priority: addForm.priority,
      recurring: addForm.recurring,
      message: addForm.message,
      assigned_to: addForm.assigned_to || profile?.id,
    };

    const { error } = editingId
      ? await supabase.from('reminders').update(payload).eq('id', editingId)
      : await supabase.from('reminders').insert({ ...payload, completed: false });

    if (error) { alert('Save failed: ' + error.message); return; }
    setShowAdd(false);
    setEditingId(null);
    resetForm();
    setVoterSearch('');
    loadReminders();
  };

  const [pendingId, setPendingId] = useState(null);

  // Build a text digest of the current view's open reminders and open WhatsApp to send to anyone.
  const sendDigest = () => {
    const active = reminders.filter(r => !r.completed);
    if (active.length === 0) { alert('No open reminders in this view to send.'); return; }
    const lines = active.map((r, i) => {
      const v = r.voter;
      const loc = v ? [v.island, v.ward, v.house].filter(Boolean).join(', ') : '';
      const contact = v?.phone ? ` · 📞 ${v.phone}` : '';
      return `${i + 1}. ${r.reminder_type}: ${v?.name || 'voter'}${loc ? ` — ${loc}` : ''}${contact} — ${formatDate(r.reminder_date)}${r.message ? ` · ${r.message}` : ''}`;
    });
    const text = `Reminders — ${filterView}\n\n${lines.join('\n')}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const snoozeReminder = async (id, newDate) => {
    setSnoozeMenuId(null);
    setPendingId(id);
    const { error } = await supabase.from('reminders').update({ reminder_date: newDate, completed: false }).eq('id', id);
    setPendingId(null);
    if (error) { alert('Snooze failed: ' + error.message); return; }
    silentRefresh();
  };

  // ── Bulk selection ──
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelectedIds(new Set());
  const bulkComplete = async () => {
    const { error } = await supabase.from('reminders').update({ completed: true, completed_at: new Date().toISOString() }).in('id', [...selectedIds]);
    if (error) { alert('Bulk complete failed: ' + error.message); return; }
    clearSelection(); silentRefresh();
  };
  const bulkSnooze = async (days) => {
    const { error } = await supabase.from('reminders').update({ reminder_date: addDays(days), completed: false }).in('id', [...selectedIds]);
    if (error) { alert('Bulk snooze failed: ' + error.message); return; }
    clearSelection(); silentRefresh();
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} reminder(s)?`)) return;
    const { error } = await supabase.from('reminders').delete().in('id', [...selectedIds]);
    if (error) { alert('Bulk delete failed: ' + error.message); return; }
    clearSelection(); silentRefresh();
  };

  const handleCompleteReminder = async (reminderId) => {
    setPendingId(reminderId);
    const now = new Date().toISOString();

    const { error } = await supabase
      .from('reminders')
      .update({ completed: true, completed_at: now })
      .eq('id', reminderId);

    if (error) {
      setPendingId(null);
      alert('Complete failed: ' + error.message);
      return;
    }

    // Verify the write actually stuck — SELECT is known to work
    const { data: row } = await supabase
      .from('reminders')
      .select('completed')
      .eq('id', reminderId)
      .single();

    setPendingId(null);

    if (!row || row.completed) {
      // Confirmed — remove from list and refresh counts
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      silentRefresh();
    } else {
      // RLS silently blocked the write
      alert(
        'Reminder was not saved — Supabase RLS is blocking UPDATE.\n\n' +
        'Fix: go to Supabase → SQL Editor and run:\n\n' +
        'CREATE POLICY "allow_update" ON reminders\n' +
        'FOR UPDATE TO authenticated USING (true);'
      );
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!confirm('Delete this reminder?')) return;
    setPendingId(reminderId);

    const { error } = await supabase
      .from('reminders')
      .delete()
      .eq('id', reminderId);

    if (error) {
      setPendingId(null);
      alert('Delete failed: ' + error.message);
      return;
    }

    // Verify the row is actually gone
    const { data: row } = await supabase
      .from('reminders')
      .select('id')
      .eq('id', reminderId)
      .single();

    setPendingId(null);

    if (!row) {
      // Confirmed deleted
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      silentRefresh();
    } else {
      // RLS silently blocked the delete
      alert(
        'Reminder was not deleted — Supabase RLS is blocking DELETE.\n\n' +
        'Fix: go to Supabase → SQL Editor and run:\n\n' +
        'CREATE POLICY "allow_delete" ON reminders\n' +
        'FOR DELETE TO authenticated USING (true);'
      );
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Urgent': '#ef4444',
      'High': '#f59e0b',
      'Medium': '#3b82f6',
      'Low': 'var(--text-3)'
    };
    return colors[priority] || 'var(--text-3)';
  };

  const getReminderIcon = (type) => ({
    'Call': 'phone',
    'Visit': 'home',
    'WhatsApp': 'message',
    'Email': 'mail',
    'Follow-Up': 'restore',
  }[type] || 'bell');

  const stats = counts;

  return (
    <div>
      {/* Header (boxed — consistent with Events / Trips / Outreach) */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6', flexShrink: 0 }}>
              <Icon name="bell" size={20} />
            </span>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Follow-ups</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Reminders</h2>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Desktop: shown inline next to Add Reminder. Mobile: moved to
                its own full-width row below so the header stays the same
                shape (icon + title + single button) as Events/Trips/Outreach. */}
            {!isMobile && reminders.some(r => !r.completed) && (
              <button onClick={sendDigest} title="Open WhatsApp with this list to send to the candidate or anyone"
                style={{ padding: '10px 16px', borderRadius: '9px', border: '1px solid var(--border)', background: 'transparent', color: '#22c55e', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <Icon name="message" size={15} /> Send to WhatsApp
              </button>
            )}
            <button onClick={toggleAdd} style={{
              padding: '10px 18px', borderRadius: '9px', border: 'none',
              background: showAdd ? 'var(--border-2)' : '#3b82f6', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
              fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'filter 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.12)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >{showAdd ? 'Cancel' : <><Icon name="plus" size={15} color="#fff" /> Add Reminder</>}</button>
          </div>
        </div>

        {isMobile && reminders.some(r => !r.completed) && (
          <button onClick={sendDigest} title="Open WhatsApp with this list to send to the candidate or anyone"
            style={{ width: '100%', padding: '10px 16px', borderRadius: '9px', border: '1px solid var(--border)', background: 'transparent', color: '#22c55e', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: '18px' }}>
            <Icon name="message" size={15} /> Send to WhatsApp
          </button>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {[
            { value: stats.total,    label: 'Total',    color: '#3b82f6', filter: 'All',      icon: 'bell' },
            { value: stats.overdue,  label: 'Overdue',  color: '#ef4444', filter: 'Overdue',  icon: 'clock' },
            { value: stats.today,    label: 'Today',    color: '#f59e0b', filter: 'Today',    icon: 'calendar' },
            { value: stats.upcoming, label: 'Upcoming', color: '#10b981', filter: 'Upcoming', icon: 'calendarRange' },
          ].map(({ value, label, color, filter, icon }) => {
            const on = filter && filterView === filter;
            return (
              <div key={label} onClick={filter ? () => setFilterView(filter) : undefined}
                style={{
                  background: 'var(--surface-2)', padding: '14px 16px', borderRadius: '12px',
                  minHeight: 96, position: 'relative', overflow: 'hidden',
                  cursor: filter ? 'pointer' : 'default',
                  border: on ? `1px solid ${color}` : '1px solid var(--border)',
                  boxShadow: on ? `0 0 0 1px ${color}` : 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => { if (filter) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; } }}
                onMouseLeave={e => { if (filter) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = on ? color : 'var(--border)'; } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                  <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                    <Icon name={icon} size={15} />
                  </span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                {filter && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, opacity: on ? 1 : 0, transition: 'opacity 0.15s' }} />}
              </div>
            );
          })}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {['All', 'Upcoming', 'Today', 'Overdue', 'Completed'].map(view => {
            const on = filterView === view;
            return (
              <button key={view} onClick={() => setFilterView(view)} style={{
                padding: '8px 16px', borderRadius: '9px',
                border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)',
                fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >{view}</button>
            );
          })}
        </div>
      </div>

      {/* Add Reminder Form */}
      {showAdd && (
        <div style={{ background: 'var(--surface)', borderRadius: 14, padding: 20, marginBottom: 20, border: '1px solid rgba(59,130,246,0.5)', boxShadow: 'var(--shadow-md)' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: 'var(--text)' }}>
            {editingId ? 'Edit Reminder' : 'Create New Reminder'}
          </h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            {/* Voter Search */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                Select Voter
              </label>
              <input
                type="text"
                value={voterSearch}
                onChange={(e) => setVoterSearch(e.target.value)}
                placeholder="Search voter by name..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-2)',
                  background: 'var(--surface-2)',
                  color: 'var(--text)',
                  fontSize: '14px'
                }}
              />
              {searchResults.length > 0 && (
                <div style={{
                  marginTop: '8px',
                  background: 'var(--surface-2)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-2)',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {searchResults.map(voter => (
                    <div
                      key={voter.id}
                      onClick={() => {
                        setAddForm({ ...addForm, voter_id: voter.id.toString(), voter_name: voter.name });
                        setVoterSearch(voter.name);
                        setSearchResults([]);
                      }}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--border-2)',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>{voter.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                        {voter.island}, {voter.ward}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Date and Type */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                  Reminder Date
                </label>
                <input
                  type="date"
                  value={addForm.reminder_date}
                  onChange={(e) => setAddForm({ ...addForm, reminder_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-2)',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                  Type
                </label>
                <select
                  value={addForm.reminder_type}
                  onChange={(e) => setAddForm({ ...addForm, reminder_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-2)',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  {REMINDER_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Priority and Recurring */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                  Priority
                </label>
                <select
                  value={addForm.priority}
                  onChange={(e) => setAddForm({ ...addForm, priority: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-2)',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  {REMINDER_PRIORITY.map(priority => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                  Recurring
                </label>
                <select
                  value={addForm.recurring}
                  onChange={(e) => setAddForm({ ...addForm, recurring: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-2)',
                    background: 'var(--surface-2)',
                    color: 'var(--text)',
                    fontSize: '14px'
                  }}
                >
                  {REMINDER_RECURRING.map(recurring => (
                    <option key={recurring} value={recurring}>{recurring}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Message */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: 'var(--text-2)' }}>
                Message/Notes
              </label>
              <textarea
                value={addForm.message}
                onChange={(e) => setAddForm({ ...addForm, message: e.target.value })}
                rows={3}
                placeholder="What needs to be done? Any specific talking points?"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-2)',
                  background: 'var(--surface-2)',
                  color: 'var(--text)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleAddReminder}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#10b981',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {editingId ? 'Save Changes' : 'Create Reminder'}
              </button>
              <button
                onClick={toggleAdd}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-2)',
                  background: 'transparent',
                  color: 'var(--text-2)',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'var(--surface)', border: '1px solid #3b82f6', boxShadow: 'var(--shadow-sm)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{selectedIds.size} selected</span>
          <div style={{ flex: 1 }} />
          <button onClick={bulkComplete} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 7, border: 'none', background: '#10b981', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Icon name="check" size={13} /> Complete</button>
          <button onClick={() => bulkSnooze(7)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Icon name="clock" size={13} /> Snooze 1w</button>
          <button onClick={bulkDelete} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: '#ef4444', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Icon name="trash" size={13} /> Delete</button>
          <button onClick={clearSelection} style={{ padding: '7px 12px', borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--text-3)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Clear</button>
        </div>
      )}

      {/* Reminders List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-2)' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-2)',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          Loading reminders...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {reminders.map(reminder => {
            const overdue = !reminder.completed && isOverdue(reminder.reminder_date);
            const soon = !reminder.completed && isSoon(reminder.reminder_date);
            const days = daysUntil(reminder.reminder_date);
            
            return (
              <div
                key={reminder.id}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  boxShadow: 'var(--shadow-sm)',
                  borderRadius: '12px',
                  padding: '20px',
                  borderLeft: `4px solid ${reminder.completed ? 'var(--text-3)' : overdue ? '#ef4444' : soon ? '#f59e0b' : getPriorityColor(reminder.priority)}`,
                  opacity: reminder.completed ? 0.6 : 1
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                  {/* Select checkbox */}
                  <input type="checkbox" checked={selectedIds.has(reminder.id)} onChange={() => toggleSelect(reminder.id)}
                    style={{ marginTop: 6, width: 16, height: 16, accentColor: '#3b82f6', cursor: 'pointer', flexShrink: 0 }} />
                  {/* Main content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Voter name */}
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 9 }}>
                      <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6', flexShrink: 0 }}>
                        <Icon name={getReminderIcon(reminder.reminder_type)} size={15} />
                      </span>
                      {reminder.voter?.name || 'Unknown Voter'}
                    </h4>

                    {/* Location and phone */}
                    {reminder.voter && (
                      <div style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <Icon name="mapPin" size={13} /> {reminder.voter.island}, {reminder.voter.ward}
                        {reminder.voter.phone && (<><span style={{ color: 'var(--text-4)' }}>·</span> <Icon name="phone" size={13} /> {reminder.voter.phone}</>)}
                      </div>
                    )}

                    {/* One-tap contact the voter */}
                    {reminder.voter && (reminder.voter.whatsapp || reminder.voter.phone) && (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                        {(reminder.voter.whatsapp || reminder.voter.phone) && (
                          <a href={waHref(reminder.voter.whatsapp || reminder.voter.phone)} target="_blank" rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: '#22c55e', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}>
                            <Icon name="message" size={13} /> WhatsApp
                          </a>
                        )}
                        {reminder.voter.phone && (
                          <a href={telHref(reminder.voter.phone)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 7, border: '1px solid var(--border)', background: 'transparent', color: '#3b82f6', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}>
                            <Icon name="phone" size={13} /> Call
                          </a>
                        )}
                      </div>
                    )}

                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: getPriorityColor(reminder.priority) + '20',
                        color: getPriorityColor(reminder.priority)
                      }}>
                        {reminder.priority}
                      </span>

                      {reminder.recurring && reminder.recurring !== 'Once' && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: '#8b5cf620',
                          color: '#8b5cf6',
                          display: 'inline-flex', alignItems: 'center', gap: 5
                        }}>
                          <Icon name="restore" size={11} /> {reminder.recurring}
                        </span>
                      )}

                      {reminder.completed && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: '#10b98120',
                          color: '#10b981',
                          display: 'inline-flex', alignItems: 'center', gap: 5
                        }}>
                          <Icon name="check" size={11} /> Completed
                        </span>
                      )}
                    </div>

                    {/* Message */}
                    {reminder.message && (
                      <p style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '12px', lineHeight: '1.6' }}>
                        {reminder.message}
                      </p>
                    )}

                    {/* Assigned to */}
                    {reminder.assigned_to_profile?.full_name && (
                      <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>
                        Assigned to: {reminder.assigned_to_profile.full_name}
                      </div>
                    )}
                  </div>

                  {/* Date and actions */}
                  <div style={{ textAlign: 'right', minWidth: '150px' }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '4px',
                      color: overdue ? '#ef4444' : soon ? '#f59e0b' : 'var(--text)'
                    }}>
                      {formatDate(reminder.reminder_date)}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '16px' }}>
                      {overdue ? `${Math.abs(days)} days overdue` :
                       days === 0 ? 'Today' :
                       days === 1 ? 'Tomorrow' :
                       `In ${days} days`}
                    </div>

                    {!reminder.completed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button
                          onClick={() => handleCompleteReminder(reminder.id)}
                          disabled={pendingId === reminder.id}
                          style={{
                            padding: '8px 16px', borderRadius: '6px', border: 'none',
                            background: pendingId === reminder.id ? 'var(--border-2)' : '#10b981',
                            color: 'white', fontSize: '13px', fontWeight: '600',
                            cursor: pendingId === reminder.id ? 'not-allowed' : 'pointer',
                            opacity: pendingId === reminder.id ? 0.6 : 1,
                          }}
                        >
                          {pendingId === reminder.id ? '…' : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Icon name="check" size={14} /> Complete</span>}
                        </button>

                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={() => setSnoozeMenuId(snoozeMenuId === reminder.id ? null : reminder.id)}
                            disabled={pendingId === reminder.id}
                            style={{
                              width: '100%', padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--border)',
                              background: 'transparent', color: 'var(--text-2)', fontSize: '13px', fontWeight: '600',
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}
                          >
                            <Icon name="clock" size={13} /> Snooze <Icon name="chevronDown" size={12} />
                          </button>
                          {snoozeMenuId === reminder.id && (
                            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 5, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: 6, minWidth: 150, textAlign: 'left' }}>
                              {[{ t: '+1 day', n: 1 }, { t: '+3 days', n: 3 }, { t: '+1 week', n: 7 }].map(o => (
                                <button key={o.n} onClick={() => snoozeReminder(reminder.id, addDays(o.n))}
                                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer', borderRadius: 6 }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                  {o.t}
                                </button>
                              ))}
                              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0', paddingTop: 6 }}>
                                <input type="date" onChange={e => e.target.value && snoozeReminder(reminder.id, e.target.value)}
                                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 12, boxSizing: 'border-box' }} />
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => startEdit(reminder)}
                            style={{
                              flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)',
                              background: 'transparent', color: 'var(--text-2)', fontSize: '13px', fontWeight: '600',
                              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            }}
                          >
                            <Icon name="edit" size={13} /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteReminder(reminder.id)}
                            disabled={pendingId === reminder.id}
                            style={{
                              flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--border)',
                              background: 'transparent', color: pendingId === reminder.id ? 'var(--text-3)' : '#ef4444',
                              fontSize: '13px', fontWeight: '600',
                              cursor: pendingId === reminder.id ? 'not-allowed' : 'pointer', opacity: pendingId === reminder.id ? 0.6 : 1,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            }}
                          >
                            <Icon name="trash" size={13} /> {pendingId === reminder.id ? '…' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    )}

                    {reminder.completed && reminder.completed_at && (
                      <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                        Completed: {formatDate(reminder.completed_at)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && reminders.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-3)' }}>
          <div style={{ display: 'inline-grid', placeItems: 'center', width: 56, height: 56, borderRadius: 14, background: 'var(--surface-2)', color: 'var(--text-4)', marginBottom: 16 }}><Icon name="bell" size={26} /></div>
          <p>No reminders found</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            {filterView !== 'All'
              ? `No ${filterView.toLowerCase()} reminders`
              : 'Click "Add Reminder" to schedule follow-ups'}
          </p>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
